import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { fullNameSchema } from "~/schemas/SignUp/FullName";
import { useFunnelTracking } from "~/shared/hooks/useFunnelTracking";
import { SignUpFormProps } from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import { Input } from "~/ui";
import { ActionButtons } from "../ActionButtons";
import { ErrorMessage } from "../ErrorMessage";
import { FormContentContainer } from "../FormContentContainer";

export const FullNameForm = ({
  onPrev,
  onNext,
  stepId = "full_name",
}: SignUpFormProps & { stepId?: string }) => {
  const { setPatientField, patient } = useSignUpStore();
  const { firstName, lastName } = patient;

  const { trackComplete, trackBack, trackErrors } = useFunnelTracking({
    step: "full_name",
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<z.infer<typeof fullNameSchema>>({
    mode: "onBlur",
    resolver: zodResolver(fullNameSchema),
    defaultValues: {
      firstName: firstName ?? "",
      lastName: lastName ?? "",
    },
  });

  const firstNameValue = watch("firstName");
  const lastNameValue = watch("lastName");
  const canProceed =
    firstNameValue?.trim() &&
    lastNameValue?.trim() &&
    !errors.firstName &&
    !errors.lastName;

  const onSubmit: SubmitHandler<z.infer<typeof fullNameSchema>> = (data) => {
    setPatientField("firstName", data.firstName);
    setPatientField("lastName", data.lastName);
    trackComplete();
    onNext();
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientField("firstName", "");
    setPatientField("lastName", "");
    trackBack();
    onPrev();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="flex w-full max-w-[570px] flex-col rounded-lg shadow-xl"
    >
      <FormContentContainer stepId={stepId}>
        <HipaaLogo className="size-16 self-center sm:size-20" />
        <h1 className="font-display text-xl sm:text-2xl">What's your name?</h1>

        <div className="flex w-full flex-col gap-4">
          <Input
            placeholder="First Name*"
            id="firstName"
            error={!!errors.firstName}
            {...register("firstName")}
          />
          <Input
            placeholder="Last Name*"
            id="lastName"
            error={!!errors.lastName}
            {...register("lastName")}
          />
        </div>

        {errors.firstName && (
          <ErrorMessage message={errors.firstName.message ?? ""} />
        )}
        {errors.lastName && (
          <ErrorMessage message={errors.lastName.message ?? ""} />
        )}
      </FormContentContainer>
      <ActionButtons
        onPrev={handlePrev}
        isNextDisabled={!canProceed}
        helperText="Enter your first and last name to continue"
      />
    </form>
  );
};
